import type { Field } from "../../api/types";
import { StaticElementCanvas } from "../../designer/SidebarElementList/types";
import { defaults } from "./defaults";
import {
  type DocumentDesignItem,
  DocumentDesignItemType,
  type DocumentDesignState,
  type FieldItem,
  FieldViewModes,
  type FileItem,
  type LinkedRecordItem,
  type MultipleSelectItem,
} from "./types";

export function generateId(): string {
  return Math.random().toString(36).substring(2);
}

export function addItem(
  state: DocumentDesignState,
  item: DocumentDesignItem,
): DocumentDesignState {
  return {
    ...state,
    items: [...state.items, item],
  };
}

export function removeItem(
  state: DocumentDesignState,
  id: string,
): DocumentDesignState {
  return {
    ...state,
    items: state.items
      .filter((item) => item.id !== id)
      .map((item) => {
        // Convert anchor to "absolute" position if it was anchored to the removed item
        if (item.anchorId === id) {
          return {
            ...item,
            position: "absolute",
            anchorId: undefined,
            anchorDistance: 0,
          };
        }

        return item;
      }),
  };
}

export function modifyItem(
  state: DocumentDesignState,
  id: string,
  modifiedItem: Omit<Partial<DocumentDesignItem>, "id" | "type">,
): DocumentDesignState {
  return {
    ...state,
    items: state.items.map((item) =>
      item.id !== id ? item : { ...item, ...modifiedItem },
    ),
  };
}

export function duplicateItem(
  state: DocumentDesignState,
  item: DocumentDesignItem,
): [DocumentDesignState, string] {
  const id = generateId();
  return [
    addItem(state, { ...item, id, top: item.top + 10, left: item.left + 10 }),
    id,
  ];
}

export function getItem(
  state: DocumentDesignState,
  id: string,
): DocumentDesignItem | undefined {
  return state.items.find((item) => item.id === id);
}

export function getItemsByType(
  state: DocumentDesignState,
  type: DocumentDesignItemType,
): DocumentDesignItem[] {
  return state.items.filter((item) => item.type === type);
}

// Used to add new items in a visible position
export const scrollBarPosition = (): number => {
  return document.getElementsByClassName("page-designer__body")[0].scrollTop;
};

export function addStaticItem(
  state: DocumentDesignState,
  type: StaticElementCanvas,
): [DocumentDesignState, string] {
  const id = generateId();
  switch (type) {
    case StaticElementCanvas.line:
      return [
        addItem(state, {
          type: DocumentDesignItemType.line,
          id,
          stroke: defaults.stroke,
          strokeWidth: 4,
          top: 200 + scrollBarPosition(),
          left: 200,
          height: 1,
          width: 200,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
        }),
        id,
      ];
    case StaticElementCanvas.rect:
      return [
        addItem(state, {
          type: DocumentDesignItemType.rect,
          id,
          top: 200 + scrollBarPosition(),
          left: 200,
          height: 100,
          width: 200,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
          fill: defaults.fill,
          stroke: defaults.stroke,
          strokeWidth: defaults.strokeWidth,
        }),
        id,
      ];
    case StaticElementCanvas.circle:
      return [
        addItem(state, {
          type: DocumentDesignItemType.circle,
          id,
          top: 200 + scrollBarPosition(),
          left: 200,
          radius: 20,
          height: 40,
          width: 40,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
          fill: defaults.fill,
          stroke: defaults.stroke,
          strokeWidth: defaults.strokeWidth,
        }),
        id,
      ];
    case StaticElementCanvas.text:
      return [
        addItem(state, {
          type: DocumentDesignItemType.text,
          id,
          template: "Start typing...",
          top: 200 + scrollBarPosition(),
          left: 200,
          width: 200,
          height: 0,
          angle: 0,
          fill: defaults.textColor,
          fontFamily: defaults.fontFamily,
          fontSize: defaults.fontSize,
          fontWeight: defaults.fontWeight,
          lineHeight: defaults.lineHeight,
          textAlign: defaults.textAlign,
          stroke: defaults.stroke,
          strokeWidth: 0,
        }),
        id,
      ];
    case StaticElementCanvas.link:
      return [
        addItem(state, {
          type: DocumentDesignItemType.link,
          id,
          template: "Example Link",
          url: "https://www.smartsuite.com/features/page-designer",
          top: 200 + scrollBarPosition(),
          left: 200,
          width: 200,
          height: 0,
          angle: 0,
          fill: defaults.linkColor,
          fontFamily: defaults.fontFamily,
          fontSize: defaults.fontSize,
          fontWeight: defaults.fontWeight,
          lineHeight: defaults.lineHeight,
          textAlign: defaults.textAlign,
          stroke: defaults.stroke,
          strokeWidth: 0,
        }),
        id,
      ];
    case StaticElementCanvas.image:
      return [
        addItem(state, {
          type: DocumentDesignItemType.image,
          id,
          top: 200 + scrollBarPosition(),
          left: 200,
          width: 150,
          height: 150,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
          fill: defaults.textColor,
          active: true,
        }),
        id,
      ];
  }
}

export function addFieldItem(
  state: DocumentDesignState,
  field: Field,
): [DocumentDesignState, string] {
  const id = generateId();
  if (field.field_type === "linkedrecordfield") {
    return [
      addItem(state, {
        type: DocumentDesignItemType.linkedRecord,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 500,
        height: 1,
        angle: 0,
        scaleX: 1,
        scaleY: 1,
        fill: defaults.textColor,
        fontFamily: defaults.fontFamily,
        fontSize: defaults.fontSize,
        fontWeight: defaults.fontWeight,
        lineHeight: defaults.lineHeight,
        textAlign: defaults.textAlign,
        field_list: ["title", "description", "full_name", "name"],
        field_linked_application: field.params.linked_application,
        mode: FieldViewModes.table,
      }),
      id,
    ];
  } else if (field.field_type === "filefield") {
    return [
      addItem(state, {
        type: DocumentDesignItemType.file,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 150,
        height: 150,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        displayMode: "first-image",
        fitMode: "fit",
        sizingMode: "square",
        imageSize: 150,
        imagePadding: 10,
      }),
      id,
    ];
  } else if (field.field_type === "multipleselectfield") {
    return [
      addItem(state, {
        type: DocumentDesignItemType.multipleSelect,
        mode: FieldViewModes.list,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 200,
        height: 0,
        fill: defaults.textColor,
        fontFamily: defaults.fontFamily,
        fontSize: defaults.fontSize,
        fontWeight: defaults.fontWeight,
        lineHeight: defaults.lineHeight,
        textAlign: defaults.textAlign,
      }),
      id,
    ];
  } else if (field.field_type === "signaturefield") {
    return [
      addItem(state, {
        type: DocumentDesignItemType.signature,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 150,
        height: 28,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
      }),
      id,
    ];
  } else if (
    ["textareafield", "richtextareafield"].includes(field.field_type)
  ) {
    return [
      addItem(state, {
        type: DocumentDesignItemType.textarea,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 200,
        height: 0,
        angle: 0,
        fill: defaults.textColor,
        fontFamily: defaults.fontFamily,
        fontSize: defaults.fontSize,
        fontWeight: defaults.fontWeight,
        lineHeight: defaults.lineHeight,
        textAlign: defaults.textAlign,
        stroke: defaults.stroke,
        strokeWidth: 0,
      }),
      id,
    ];
  } else {
    return [
      addItem(state, {
        type: DocumentDesignItemType.field,
        id,
        field_slug: field.slug,
        top: 200 + scrollBarPosition(),
        left: 200,
        width: 200,
        height: 0,
        angle: 0,
        fill: defaults.textColor,
        fontFamily: defaults.fontFamily,
        fontSize: defaults.fontSize,
        fontWeight: defaults.fontWeight,
        lineHeight: defaults.lineHeight,
        textAlign: defaults.textAlign,
        stroke: defaults.stroke,
        strokeWidth: 0,
      }),
      id,
    ];
  }
}

export function orderItems(
  state: DocumentDesignState,
  orderedIds: string[],
): DocumentDesignState {
  return {
    ...state,
    items: orderedIds
      .map((id) => state.items.find((item) => item.id === id))
      .filter(Boolean) as DocumentDesignItem[],
  };
}

export const hasSelectedField = (
  item: DocumentDesignItem | undefined,
): item is FieldItem | LinkedRecordItem | MultipleSelectItem | FileItem => {
  if (!item) {
    return false;
  }

  return [
    DocumentDesignItemType.field,
    DocumentDesignItemType.textarea,
    DocumentDesignItemType.linkedRecord,
    DocumentDesignItemType.multipleSelect,
    DocumentDesignItemType.file,
  ].includes(item.type);
};
